/** /preflight-items — the system-wide Preflight → Items grid.
 *
 * Cross-tenant list of per-TO checklist items with filters (tenant / user group /
 * user / area / show), search, and a row drawer reusing the Preflight tab's detail.
 * By default the backend scopes rows to the caller's own groups; the
 * console_preflight_items_view_others permission widens it. The "Show" filter is
 * "All open" (completed hidden) by default, "All including completed", or a single
 * status (pending / started / completed / error). */
import {
  Box,
  Chip,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
} from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid-pro';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';

import {
  groups as preflightGroups,
  items,
  userGroups,
  type PreflightItem,
  type PreflightStatus,
} from '@/api/preflight';
import { list as listUsers } from '@/api/users';
import {
  ResourceDataGrid,
  type ResourceDataGridParams,
} from '@/components/DataGrid/ResourceDataGrid';
import { PageHeader } from '@/components/PageHeader';
import { RemoteAutocomplete } from '@/components/RemoteAutocomplete/RemoteAutocomplete';
import { WithTenantMark } from '@/components/TenantMark/TenantMark';
import { TenantPicker } from '@/components/TenantPicker/TenantPicker';
import { AssignedGroupLink } from '@/features/preflight/AssignedGroupLink';
import { PreflightItemDrawer } from '@/features/preflight/PreflightItemDrawer';
import { PAGE_FILL_SX } from '@/layout/pageFill';
import { formatTenantIds } from '@/scope/tenantIds';
import { useScopedTenantsState } from '@/scope/useScopedTenantsState';

const STATUS_CHIP: Record<PreflightStatus, 'default' | 'info' | 'success' | 'error'> = {
  pending: 'default',
  started: 'info',
  completed: 'success',
  error: 'error',
  // Offboarding items only (the grid lists the onboarding checklist unless asked).
  skipped: 'info',
  superseded: 'default',
};

interface PickOption {
  id: number;
  label: string;
}

export function PreflightItemsListPage() {
  const qc = useQueryClient();
  // The operators (several, D1); they follow the console scope until picked here.
  const [tenants, setTenants] = useScopedTenantsState();
  const tenantIds = formatTenantIds(tenants.map((t) => t.id));
  const [userGroup, setUserGroup] = useState<string>('');
  const [user, setUser] = useState<PickOption | null>(null);
  const [itemGroup, setItemGroup] = useState<string>('');
  const [show, setShow] = useState<string>('open');
  const [selected, setSelected] = useState<PreflightItem | null>(null);

  const { data: userGroupsData } = useQuery({
    queryKey: ['preflight', 'user-groups', 'options'],
    queryFn: () => userGroups.list({ pageSize: 100 }),
  });
  const { data: itemGroupsData } = useQuery({
    queryKey: ['preflight', 'groups', 'options'],
    queryFn: () => preflightGroups.list({ pageSize: 200, ordering: 'sequence' }),
  });

  const extra = useMemo(
    () => ({
      // The endpoint's tenant param is `tenant` (not `tour_operator_id`).
      ...(tenantIds ? { tenant: tenantIds } : {}),
      ...(userGroup ? { user_group: userGroup } : {}),
      ...(user ? { user: user.id } : {}),
      ...(itemGroup ? { item_group: Number(itemGroup) } : {}),
      // "open" (default) hides completed; "all" includes everything; anything else is a
      // specific status (the backend applies it and skips the completed-exclusion).
      ...(show === 'all' ? { show_completed: 'true' } : show === 'open' ? {} : { status: show }),
    }),
    [tenantIds, userGroup, user, itemGroup, show],
  );

  const queryFn = useCallback(
    (p: ResourceDataGridParams) =>
      items.listAll({
        page: p.page,
        pageSize: p.pageSize,
        ordering: p.ordering ?? '-updated_ts',
        search: p.search,
        tenant: p.extra?.tenant as string | undefined,
        userGroup: p.extra?.user_group as string | undefined,
        user: p.extra?.user as number | undefined,
        status: p.extra?.status as PreflightStatus | undefined,
        itemGroup: p.extra?.item_group as number | undefined,
        showCompleted: p.extra?.show_completed === 'true',
      }),
    [],
  );

  const columns = useMemo<GridColDef<PreflightItem>[]>(
    () => [
      {
        field: 'tour_operator',
        headerName: 'Tenant',
        width: 170,
        valueGetter: (_, row) => row.tour_operator?.name ?? '—',
        renderCell: (p) => (
          <WithTenantMark environment={p.row.tour_operator?.environment}>{p.value}</WithTenantMark>
        ),
      },
      { field: 'title', headerName: 'Item', flex: 1, minWidth: 220, sortable: false },
      { field: 'group_name', headerName: 'Area', width: 170, sortable: false },
      {
        field: 'assigned_group',
        headerName: 'User group',
        width: 160,
        renderCell: (p) => <AssignedGroupLink item={p.row} />,
      },
      {
        field: 'owner',
        headerName: 'Owner',
        width: 140,
        valueGetter: (_, row) => row.owner?.name ?? '—',
      },
      {
        field: 'status',
        headerName: 'Status',
        width: 130,
        renderCell: (p) => (
          <Chip size="small" color={STATUS_CHIP[p.row.status]} label={p.row.status} />
        ),
      },
      {
        field: 'updated_ts',
        headerName: 'Updated',
        width: 170,
        valueGetter: (_, row) => new Date(row.updated_ts).toLocaleString(),
      },
      // Supplementary columns — hidden by default (columns menu), display-only:
      // none of these fields are in the backend `_ORDERING` map, so a header sort
      // would silently no-op.
      {
        field: 'action',
        headerName: 'Action',
        width: 210,
        sortable: false,
        renderCell: (p) => (
          <Box component="span" sx={{ fontFamily: 'monospace', fontSize: 12 }}>
            {p.row.action}
          </Box>
        ),
      },
      {
        field: 'task_type',
        headerName: 'Type',
        width: 110,
        sortable: false,
        renderCell: (p) => <Chip size="small" variant="outlined" label={p.row.task_type} />,
      },
      {
        field: 'owner_role',
        headerName: 'Owner role',
        width: 120,
        sortable: false,
        renderCell: (p) =>
          p.row.owner_role ? (
            <Chip size="small" variant="outlined" label={p.row.owner_role} />
          ) : null,
      },
      {
        field: 'implemented',
        headerName: 'Implemented',
        width: 120,
        sortable: false,
        renderCell: (p) =>
          p.row.implemented ? (
            <Chip size="small" color="success" variant="outlined" label="yes" />
          ) : (
            <Chip size="small" color="warning" label="stub" />
          ),
      },
      {
        field: 'applicable',
        headerName: 'Applicable',
        width: 110,
        sortable: false,
        renderCell: (p) =>
          p.row.applicable ? (
            <Chip size="small" color="success" variant="outlined" label="yes" />
          ) : (
            <Chip size="small" label="no" title={p.row.applicable_reason || undefined} />
          ),
      },
      {
        field: 'optional',
        headerName: 'Optional',
        width: 100,
        sortable: false,
        renderCell: (p) => (p.row.optional ? 'Optional' : '—'),
      },
      {
        field: 'error_message',
        headerName: 'Error',
        flex: 1,
        minWidth: 160,
        sortable: false,
        renderCell: (p) =>
          p.row.error_message ? (
            <Box
              component="span"
              title={p.row.error_message}
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                color: 'error.main',
              }}
            >
              {p.row.error_message}
            </Box>
          ) : null,
      },
      {
        field: 'created_ts',
        headerName: 'Created',
        width: 170,
        sortable: false,
        valueGetter: (_, row) => (row.created_ts ? new Date(row.created_ts).toLocaleString() : ''),
      },
    ],
    [],
  );

  const searchUsers = async (q: string): Promise<PickOption[]> => {
    const res = await listUsers({ kind: 'system', search: q, pageSize: 20, is_active: true });
    return res.results.map((u) => ({
      id: u.id,
      label: `${u.first_name} ${u.last_name}`.trim() || u.email,
    }));
  };

  return (
    <Box sx={PAGE_FILL_SX}>
      <PageHeader
        helpStory="preflight-items-page"
        title="Preflight Items"
        subtitle="Every tour operator's onboarding items in one place. You see items for the groups you belong to unless you have the view-others permission."
      />

      <Stack direction="row" spacing={1.5} sx={{ mb: 2, flexWrap: 'wrap', gap: 1.5 }}>
        <Box sx={{ width: 240 }}>
          <TenantPicker multiple fieldWidth={240} value={tenants} onChange={setTenants} />
        </Box>
        <FormControl size="small" sx={{ minWidth: 170 }}>
          <InputLabel>User group</InputLabel>
          <Select
            label="User group"
            value={userGroup}
            onChange={(e) => setUserGroup(e.target.value)}
          >
            <MenuItem value="">All</MenuItem>
            {(userGroupsData?.results ?? []).map((g) => (
              <MenuItem key={g.owner_role} value={g.owner_role}>
                {g.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Box sx={{ width: 220 }}>
          <RemoteAutocomplete<PickOption>
            size="small"
            label="User"
            placeholder="Any member"
            fetchOnEmpty
            value={user}
            onChange={(v) => setUser((v as PickOption | null) ?? null)}
            queryFn={(q) => searchUsers(q)}
            getOptionLabel={(o) => o.label}
            getOptionKey={(o) => o.id}
          />
        </Box>
        <FormControl size="small" sx={{ minWidth: 210 }}>
          <InputLabel>Show</InputLabel>
          <Select label="Show" value={show} onChange={(e) => setShow(e.target.value)}>
            <MenuItem value="open">All open</MenuItem>
            <MenuItem value="all">All including completed</MenuItem>
            <Divider />
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="started">Started</MenuItem>
            <MenuItem value="completed">Completed</MenuItem>
            <MenuItem value="error">Error</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Area</InputLabel>
          <Select label="Area" value={itemGroup} onChange={(e) => setItemGroup(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            {(itemGroupsData?.results ?? []).map((g) => (
              <MenuItem key={g.id} value={String(g.id)}>
                {g.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>

      <ResourceDataGrid<PreflightItem>
        queryKey={['preflight', 'all-items']}
        queryFn={queryFn}
        columns={columns}
        extra={extra}
        defaultSort={{ field: 'updated_ts', sort: 'desc' }}
        initialState={{
          columns: {
            columnVisibilityModel: {
              action: false,
              task_type: false,
              owner_role: false,
              implemented: false,
              applicable: false,
              optional: false,
              error_message: false,
              created_ts: false,
            },
          },
        }}
        emptyMessage="No preflight items match these filters."
        headerToolbar={{
          search: true,
          searchPlaceholder: 'Item or area name',
          exportFileName: 'preflight-items',
        }}
        onRowClick={(_id, row) => setSelected(row)}
      />

      <PreflightItemDrawer
        selected={selected}
        onClose={() => setSelected(null)}
        onChanged={() => void qc.invalidateQueries({ queryKey: ['preflight', 'all-items'] })}
      />
    </Box>
  );
}
